import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Eye, EyeOff, KeyRound, Check, X } from "lucide-react";

// HIPAA-compliant password requirements
const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 12 characters', test: (p) => p.length >= 12 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'One digit', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special character (!@#$%...)', test: (p) => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(p) },
];

export default function ChangePassword() {
  const { user, clearMustChangePassword, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Check password requirements
  const passwordChecks = useMemo(() => {
    return PASSWORD_REQUIREMENTS.map(req => ({
      ...req,
      passed: req.test(form.new_password)
    }));
  }, [form.new_password]);

  const allRequirementsMet = passwordChecks.every(check => check.passed);

  // Redirect to login if not authenticated
  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.new_password !== form.confirm_password) {
      setError("New passwords do not match");
      return;
    }
    
    if (!allRequirementsMet) {
      setError("Password does not meet all security requirements");
      return;
    }

    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      clearMustChangePassword();
      // Redirect to appropriate dashboard
      if (user.role === "collections") navigate("/dashboard/collections", { replace: true });
      else if (user.role === "location") navigate("/crm-queue", { replace: true });
      else navigate("/dashboard/sales", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const isForced = user.must_change_password;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-8 py-6 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                <KeyRound size={18} className="text-blue-700" />
              </div>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {isForced ? "Set Your Password" : "Change Password"}
              </h1>
            </div>
            {isForced && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 flex items-center gap-2">
                <Shield size={14} className="flex-shrink-0" />
                For security, you must set a new password before continuing.
              </p>
            )}
            {!isForced && (
              <p className="text-sm text-slate-500 mt-1">Signed in as <span className="font-semibold text-slate-700">{user.username}</span></p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
            {error && (
              <div data-testid="change-password-error" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            {/* Current password */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {isForced ? "Temporary Password" : "Current Password"}
              </label>
              <div className="relative">
                <input
                  data-testid="current-password-input"
                  type={showCurrent ? "text" : "password"}
                  required
                  value={form.current_password}
                  onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))}
                  className="w-full h-10 pl-3 pr-10 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder={isForced ? "Enter your temporary password" : "Enter current password"}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">New Password</label>
              <div className="relative">
                <input
                  data-testid="new-password-input"
                  type={showNew ? "text" : "password"}
                  required
                  value={form.new_password}
                  onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
                  className="w-full h-10 pl-3 pr-10 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="Min. 12 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              
              {/* Password Requirements Checklist */}
              {form.new_password.length > 0 && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs font-medium text-slate-600 mb-2">Password Requirements:</p>
                  <div className="grid grid-cols-2 gap-1">
                    {passwordChecks.map(check => (
                      <div key={check.id} className="flex items-center gap-1.5">
                        {check.passed ? (
                          <Check size={12} className="text-emerald-500" />
                        ) : (
                          <X size={12} className="text-slate-300" />
                        )}
                        <span className={`text-xs ${check.passed ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {check.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm new password */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Confirm New Password</label>
              <input
                data-testid="confirm-password-input"
                type="password"
                required
                value={form.confirm_password}
                onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
                className="w-full h-10 pl-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="Repeat new password"
              />
              {form.confirm_password && form.new_password !== form.confirm_password && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              {!isForced && (
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex-1 h-10 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                data-testid="change-password-submit"
                type="submit"
                disabled={saving}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {saving ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <KeyRound size={15} />
                )}
                {saving ? "Saving…" : "Set New Password"}
              </button>
            </div>

            {isForced && (
              <button
                type="button"
                onClick={logout}
                className="w-full text-xs text-slate-400 hover:text-slate-600 text-center mt-1 transition-colors"
              >
                Sign out instead
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
